import { Avatar, Flex, Td, Text, Tr } from '@chakra-ui/react';
import {
  OrganizationPartnerItemFragment,
  PartnerPreferenceType,
  PartnerType,
} from '@client/graphql/__generated__/types';
import { memo, useCallback } from 'react';
import { ContactableSelect } from './ContactableSelect';
import { PreferredPartnerSelect } from './PreferredPartnerSelect';
import { RosterEntry } from './types';

interface RosterTableRowProps {
  entry: RosterEntry;
  partners: OrganizationPartnerItemFragment[];
  onUpdatePreference: (
    rosterEntryId: string,
    partnerType: PartnerType,
    preferenceType: PartnerPreferenceType,
    partnerId?: string | null,
    partnerContactId?: string | null
  ) => void;
  onUpdateContactable: (rosterEntryId: string, isContactable: boolean) => void;
  isUpdating?: boolean;
}

export const RosterTableRow = memo(function RosterTableRow({
  entry,
  partners,
  onUpdatePreference,
  onUpdateContactable,
  isUpdating,
}: RosterTableRowProps) {
  const { agent, preferences, isContactable } = entry;
  const fullName = `${agent.firstName} ${agent.lastName}`;

  const handlePreferenceChange = useCallback(
    (partnerType: PartnerType) =>
      (
        preferenceType: PartnerPreferenceType,
        partnerId?: string | null,
        partnerContactId?: string | null
      ) => {
        onUpdatePreference(
          entry.id,
          partnerType,
          preferenceType,
          partnerId,
          partnerContactId
        );
      },
    [entry.id, onUpdatePreference]
  );

  const handleContactableChange = useCallback(
    (value: boolean) => {
      onUpdateContactable(entry.id, value);
    },
    [entry.id, onUpdateContactable]
  );

  // Placeholder annual transactions (deterministic based on entry ID)
  const annualTransactions =
    (entry.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      50) +
    10;

  return (
    <Tr
      _hover={{ bg: 'whiteAlpha.50' }}
      borderBottom="1px solid"
      borderColor="whiteAlpha.100"
    >
      {/* Agent */}
      <Td border="none" px={5} py={3}>
        <Flex alignItems="center" gap={3}>
          <Avatar
            bg="purple.500"
            color="white"
            name={fullName}
            size="sm"
            src={undefined}
          />
          <Text color="white" fontSize="sm" fontWeight="medium">
            {fullName}
          </Text>
        </Flex>
      </Td>

      {/* Preferred Lender */}
      <Td border="none" px={5} py={3}>
        <PreferredPartnerSelect
          isDisabled={isUpdating}
          partnerType={PartnerType.LENDER}
          partners={partners.filter((p) => p.partnerType === PartnerType.LENDER)}
          preference={preferences[PartnerType.LENDER]}
          onChange={handlePreferenceChange(PartnerType.LENDER)}
        />
      </Td>

      {/* Preferred Title */}
      <Td border="none" px={5} py={3}>
        <PreferredPartnerSelect
          isDisabled={isUpdating}
          partnerType={PartnerType.TITLE}
          partners={partners.filter((p) => p.partnerType === PartnerType.TITLE)}
          preference={preferences[PartnerType.TITLE]}
          onChange={handlePreferenceChange(PartnerType.TITLE)}
        />
      </Td>

      {/* Preferred Insurance */}
      <Td border="none" px={5} py={3}>
        <PreferredPartnerSelect
          isDisabled={isUpdating}
          partnerType={PartnerType.INSURANCE}
          partners={partners.filter(
            (p) => p.partnerType === PartnerType.INSURANCE
          )}
          preference={preferences[PartnerType.INSURANCE]}
          onChange={handlePreferenceChange(PartnerType.INSURANCE)}
        />
      </Td>

      {/* Annual Transactions (placeholder) */}
      <Td border="none" px={5} py={3}>
        <Text color="whiteAlpha.700" fontSize="sm">
          {annualTransactions}
        </Text>
      </Td>

      {/* Contactable */}
      <Td border="none" px={5} py={3}>
        <ContactableSelect
          isDisabled={isUpdating}
          value={isContactable}
          onChange={handleContactableChange}
        />
      </Td>
    </Tr>
  );
});
